import { Injector } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { AuthService } from '../../../shared/service/auth-service/auth-service.service';
import { Art57Section } from '../../../shared/ui/Art57Section/Art57Section';
import {
  distinctUntilChanged,
  filter,
  Observable,
  startWith,
  takeUntil,
} from 'rxjs';
import { LookupGetService } from '../../../shared/service/lookup-service/lookup-get.service';
import { CodeName } from '../../../shared/service/lookup-service/code-name.model';
import { DateUtil } from '../../../shared/ui/DateUtil';
import { PLACEHOLDER } from '../../../shared/ui/Constants';
import { placeholderEnum } from '../../../shared/common/enum/bulkExportEnum';
import { BulkUpdateSection } from '../../../shared/ui/Art57Section/BulkUpdateSection';

export class FieldDefinition {
  constructor(private injector: Injector) { }

  /**
   * Recursively searches the JSON tree to find the node with the matching path.
   * @param node Current node to inspect.
   * @param targetPath Path to search
   */
  private findNodeByPath(node: any, targetPath: string): any | null {
    if (!node) return null;

    if (node.xpath === targetPath) {
      return node;
    }

    if (node.children) {
      // children is an object with dynamic keys
      for (const key of Object.keys(node.children)) {
        const child = node.children[key];
        const result = this.findNodeByPath(child, targetPath);
        if (result) return result;
      }
    }

    return null;
  }

  private findFilterOperation(targetPath: string): Observable<CodeName[]> {
    // Root of your structure (as per your original code)
    const root =
      this.injector.get(Art57Section).bulkupdate.actionCache.tuplesJson
        .authorisedproducts;

    const node = this.findNodeByPath(root, targetPath);
    let lookupName = node.lookupFilter;
    if (
      node &&
      node.isFilterDynamic &&
      node.values !== undefined &&
      node.values !== null &&
      node.values.length > 0
    ) {
      lookupName = 'dyn' + lookupName;
    }

    return this.injector.get(LookupGetService).retrieveFromCache(lookupName);
  }

  private findChangeAction(targetPath: string): Observable<CodeName[]> {
    // Root of your structure (as per your original code)
    const root =
      this.injector.get(Art57Section).bulkupdate.actionCache.tuplesJson
        .authorisedproducts;

    let node = this.findNodeByPath(root, targetPath);
    let lookupName = node.lookupChange;

    if (node !== undefined && node !== null && node.isChangeDynamic) {
      const changeXpath =
        node.changeXpath !== undefined && node.changeXpath !== null
          ? node.changeXpath
          : undefined;

      if (node.changeXpath) {
        node = this.findNodeByPath(root, changeXpath);
      }
      if (
        node !== undefined &&
        node !== null &&
        node.values !== undefined &&
        node.values !== null &&
        node.values.length > 0
      ) {
        lookupName = 'dyn' + lookupName;
      }
    }

    return this.injector.get(LookupGetService).retrieveFromCache(lookupName);
  }

  private getNodeInfo(targetPath: string): {
    lookupName: string | undefined;
    componentType: string;
    allowDateFormat: boolean | undefined;
    xpathDateFormat: string | undefined;
  } {
    const root =
      this.injector.get(Art57Section).bulkupdate.actionCache.tuplesJson
        .authorisedproducts;

    let node = this.findNodeByPath(root, targetPath);
    if (node.changeXpath !== undefined && node.changeXpath !== null) {
      node = this.findNodeByPath(root, node.changeXpath);
    }

    const lookupName = node.lookupValue ?? undefined;
    const componentType = lookupName
      ? 'select'
      : node.allowDateFormat == undefined
        ? 'input'
        : 'datetime'; // JMBL add a variable allowDateFormat in Backend configuration properties file?
    const allowDateFormat =
      componentType === 'datetime' ? node.allowDateFormat === true : false;

    const xpathDateFormat =
      componentType === 'datetime' ? node.xpathDateFormat : undefined;

    return {
      lookupName: lookupName,
      componentType: componentType,
      allowDateFormat: allowDateFormat,
      xpathDateFormat: xpathDateFormat
    };
  }

  private getLeafNodeInfo(targetPath: string): {
    leaf: string;
    label: string;
    componentType: string;
    lookupName: any;
  } {
    // Root of your structure (as per your original code)
    const root =
      this.injector.get(Art57Section).bulkupdate.actionCache.tuplesJson
        .authorisedproducts;

    let node = this.findNodeByPath(root, targetPath);
    let redirected = false;
    if (node.changeXpath !== undefined && node.changeXpath !== null) {
      redirected = true;
      node = this.findNodeByPath(root, node.changeXpath);
    }
    const leafValue = node.xpath.split('/').at(-1);
    const labelValue = redirected ? (node.desc ?? undefined) : undefined;
    const componentType = node.lookupValue ? 'select' : 'input';

    return {
      leaf: leafValue,
      label: labelValue,
      componentType: componentType,
      lookupName: node.lookupValue,
    };
  }

  private getChooseableValues(targetPath: string): CodeName[] {
    // Root of your structure (as per your original code)
    const root =
      this.injector.get(Art57Section).bulkupdate.actionCache.tuplesJson
        .authorisedproducts;

    let node = this.findNodeByPath(root, targetPath);
    let result: CodeName[] = [];
    if (node.changeXpath !== undefined && node.changeXpath !== null) {
      node = this.findNodeByPath(root, node.changeXpath);
    }

    if (
      node &&
      node.values !== undefined &&
      node.values !== null &&
      node.values &&
      node.values.length > 0
    ) {
      let isDate = false;
      if (node.allowDateFormat !== undefined && node.allowDateFormat !== null) {
        isDate = true;
      }

      node.values.forEach((item: any, index: number) => {
        let itemName =
          item.value && item.valuedecoded ? '[' + item.value + '] ' : '';
        if (isDate) {
          itemName =
            '[' +
            item.value +
            '] - ' +
            (item.value.length == 8
              ? DateUtil.importDate(item.value, 102)
              : DateUtil.importDate(item.value, 610));
        } else {
          itemName = itemName + (item.valuedecoded ?? item.value);
        }
        itemName = itemName + ' (' + item.count + ')';
        result.push({ code: item.value, name: itemName });
      });
    }

    return result;
  }

  private findTerminalChildrenByPath(
    targetPath: string,
    hideAllowed: string
  ): Array<{ code: string; name: string }> {
    function addAttribute(node: any, resultArray: any) {
      if (node) {
        resultArray.push({
          code: node.xpath,
          name: node.desc ?? '',
        });
      }
    }
    /**
     * Collects ONLY the immediate children of the given node
     * @param node Node whose direct children will be inspected.
     */
    function collectForActionsChildren(
      node: any,
    ): Array<{ code: string; name: string }> {
      const results: Array<{ code: string; name: string }> = [];

      if (!node || !node.children) return results;

      for (const key of Object.keys(node.children)) {
        // If exists terminal children (attributes)
        const child = node.children[key];
        if (child.isForActions == true) {
          let addNode = true;
          switch (hideAllowed) {
            case 'F':
              if (child.filterHide != undefined && child.filterHide != null) {
                addNode = !child.filterHide;
              }
              break;
            case 'C':
              if (child.changeHide != undefined && child.changeHide != null) {
                addNode = !child.changeHide;
              }
              break;
          }
          if (addNode) {
            addAttribute(child, results);
          }
        }
      }

      return results;
    }

    // Root of your structure (as per your original code)
    const root =
      this.injector.get(Art57Section).bulkupdate.actionCache.tuplesJson
        .authorisedproducts;

    // 1) Find the node that matches the provided path
    const startNode = this.findNodeByPath(root, targetPath);
    if (!startNode) {
      // Path not found → return empty list
      return [];
    }

    // 2) Return ONLY the immediate terminal children (do not traverse deeper)
    let result = collectForActionsChildren(startNode);

    result = result.sort((a, b) => a.name.localeCompare(b.name));

    return result;
  }

  title: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'flex-grow-1 me-3',
          key: 'filterTitle',
          type: 'input',
          props: {
            label: 'Request Title',
            placeholder: 'Enter title',
            tooltip: 'Name of your request',
            fieldDefinition: 'Name of your request',
            attributes: {
              title: 'Assign a name to identify your request',
            },
            maxLength: 50,
          },
        },
        {
          key: 'bulkUpdate',
          defaultValue: true,
        },
      ],
    },
  ];

  monitor: FormlyFieldConfig[] = [
    {
      key: 'requestExecuting',
      defaultValue: 0,
      type: 'input',
      hide: true,
    },
  ];

  downloads: FormlyFieldConfig[] = [{}];

  filters: FormlyFieldConfig[] = [
    {
      key: 'versionToExport',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          props: {
            disabled: true,
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          defaultValue: 6,
          type: 'bulkRadioType',
          props: {
            label: 'Version to Export',
            lookupname: 'versiontoexport',
            valueProp: 'code',
            labelProp: 'name',
            showClear: false,
            fieldDescription:
              'Select which product version should be exported (last version or last version assessed as valid)',
            hidden: false,
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'assessment',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          defaultValue: null,
          type: 'bulkRadioType',
          props: {
            label: 'Version Assessment',
            lookupname: 'pendingValues',
            valueProp: 'code',
            labelProp: 'name',
            fieldDescription:
              'Select what should be the assessment status of the product entity as present in the product pending field within the AMP',
            showClear: true,
            hidden: true,
          },
        },
      ],
    },
    { type: 'template', template: '<hr  />' },
    {
      key: 'art57Format',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          defaultValue: null,
          type: 'bulkRadioType',
          props: {
            label: 'Article 57 Format',
            lookupname: 'bulkart57Format',
            valueProp: 'code',
            labelProp: 'name',
            fieldDescription:
              'Select in which format the product entity was submitted',
            showClear: true,
            hidden: true,
          },
        },
      ],
    },
    { type: 'template', template: '<hr  />' },
    {
      key: 'interimFormat',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          defaultValue: null,
          type: 'bulkRadioType',
          props: {
            label: 'Interim Format',
            lookupname: 'bulkinterimFormat',
            valueProp: 'code',
            labelProp: 'name',
            fieldDescription:
              'Select in which format the product entity was submitted',
            showClear: true,
            hidden: true,
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'startDate',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          defaultValue: null,
          type: 'bulkDateType',
          props: {
            label: 'Last Update (From)',
            fieldDescription:
              'Select the start date when the product was last updated',
            showClear: true,
            hidden: true,
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'endDate',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          defaultValue: null,
          type: 'bulkDateType',
          props: {
            label: 'Last Update (To)',
            fieldDescription:
              'Select the end date when the product was last updated',
            showClear: true,
            hidden: true,
          },
        },
      ],
    },
    {
      key: 'authorisedDevelopment',
      defaultValue: 2,
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'drugingredients',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'Ingredient',
            fieldDescription:
              'Enter or select substance(s) referenced in the last version of products owned by your HQ organisation. Multiple selection allowed',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'bulkdrugingredient',
                  placeholder: 'Select items',
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: true,
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'pharmaceuticalforms',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'Pharmaceutical Form',
            fieldDescription:
              'Enter or select pharmaceutical form(s) referenced in the last version of products owned by your HQ organisation. Multiple selection allowed',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'bulkpharmaceuticalform',
                  placeholder: 'Select items',
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: true,
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'atccodes',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'ATC Code',
            fieldDescription:
              'Enter or select ATC Code(s) referenced in the last version of products owned by your HQ organisation. Multiple selection allowed',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'bulkatccode',
                  placeholder: 'Select items',
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: true,
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'indicationmeddracodes',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'Indication MedDRA Code',
            fieldDescription:
              'Enter or select MedDRA Code(s) referenced in the last version of products owned by your HQ organisation. Multiple selection allowed',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'indicationmeddracode',
                  placeholder: 'MedDRA Codes',
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: true,
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'organisations',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'MAH',
            fieldDescription:
              'Enter or select MAH organisation(s) referenced in the last version of AMPs owned by your HQ organisation. Multiple selection allowed',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'bulkorganisation',
                  placeholder: 'MAH',
                  parameters: [['type', 1]],
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: true,
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },

    {
      key: 'qppvs',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
          hideExpression: (field) => {
            return this.injector.get(AuthService).isDatamaskedUser();
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'bulkLookupListType',
          props: {
            label: 'QPPV',
            fieldDescription:
              'Select QPPV(s) referenced in the last version of AMPs owned by your HQ organisation. Multiple selection allowed',
            lookupname: 'bulkqppv',
            valueProp: 'code',
            labelProp: 'name',
            hidden: true,
            size: 5,
            showCode: true,
            showSearchField: this.injector
              .get(AuthService)
              .isEMAorNCAOrganisation(),
          },
          hideExpression: (field) => {
            return (
              this.injector.get(AuthService).isDatamaskedUser() ||
              field?.props?.hidden
            );
          },
        },
        {
          key: 'datasource',
        },
        {
          key: 'operator',
          defaultValue: 'cn',
        },
        {
          key: 'term',
        },
        {
          key: 'lastOrder',
        },
      ],
    },
    {
      type: 'template',
      template: '<hr />',
      hideExpression: (field) => {
        return (
          this.injector.get(AuthService).isDatamaskedUser() ||
          field.props?.hidden
        );
      },
    },
    {
      key: 'mfls',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'bulkLookupListType',
          props: {
            label: 'Master File Location',
            fieldDescription:
              'Select MFL(s) referenced in the last version of AMPs owned by your HQ organisation. Multiple selection allowed',
            lookupname: 'bulkmfl',
            valueProp: 'code',
            labelProp: 'name',
            hidden: true,
            size: 5,
            showCode: true,
            showSearchField: this.injector
              .get(AuthService)
              .isEMAorNCAOrganisation(),
          },
        },
        {
          key: 'datasource',
        },
        {
          key: 'operator',
          defaultValue: 'cn',
        },
        {
          key: 'term',
        },
        {
          key: 'lastOrder',
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'ownerorganisations',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'bulkLookupListType',
          props: {
            label: 'Sender Organisation',
            fieldDescription:
              'Select the organisation(s) that submitted the last version of products owned by your HQ organisation. Multiple selection allowed',
            lookupname: 'owneridsproduct',
            valueProp: 'code',
            labelProp: 'name',
            hidden: true,
            size: 5,
            showCode: true,
            showSearchField: false,
          },
        },
        {
          key: 'datasource',
        },
        {
          key: 'operator',
          defaultValue: 'cn',
        },
        {
          key: 'term',
        },
        {
          key: 'lastOrder',
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'authorisationcountrycodes',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'bulkLookupListType',
          props: {
            label: 'Authorisation Country Code',
            fieldDescription:
              'Select authorisation country code(s) referenced in the last version of products owned by your HQ organisation. Multiple selection allowed',
            lookupname: 'authorisationcountrycode',
            valueProp: 'code',
            labelProp: 'name',
            hidden: true,
            size: 5,
            showCode: true,
            showSearchField: false,
          },
        },
        {
          key: 'datasource',
        },
        {
          key: 'operator',
          defaultValue: 'cn',
        },
        {
          key: 'term',
        },
        {
          key: 'lastOrder',
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'authorisationprocedures',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'bulkLookupListType',
          props: {
            label: 'Authorisation Procedure',
            fieldDescription:
              'Select authorisation procedure(s) referenced in the last version of products owned by your HQ organisation. Multiple selection allowed',
            lookupname: 'bulkauthorisationprocedure',
            valueProp: 'code',
            labelProp: 'name',
            hidden: true,
            size: 5,
            showCode: false,
            showSearchField: false,
          },
        },
        {
          key: 'datasource',
        },
        {
          key: 'operator',
          defaultValue: 'cn',
        },
        {
          key: 'term',
        },
        {
          key: 'lastOrder',
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'authorisationstatuses',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'bulkLookupListType',
          props: {
            label: 'Authorisation Status',
            fieldDescription:
              'Select authorisation status(es) referenced in the last version of products owned by your HQ organisation. Multiple selection allowed',
            lookupname: 'bulkauthorisationstatus',
            valueProp: 'code',
            labelProp: 'name',
            hidden: true,
            size: 5,
            showCode: false,
            showSearchField: false,
          },
        },
        {
          key: 'datasource',
        },
        {
          key: 'operator',
          defaultValue: 'cn',
        },
        {
          key: 'term',
        },
        {
          key: 'lastOrder',
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'anynames',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'Product Any Name',
            fieldDescription:
              'Enter or select any product name(s) referenced in the last version of products owned by your HQ organisation. Multiple selection allowed',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'anyname',
                  placeholder: 'Any Name',
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: false,
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'fullnames',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'Full Presentation Name',
            fieldDescription:
              'Enter or select any product name(s) referenced in the ' + "'" + 'Full Presentation Name' + "'" + ' field of the last version of products owned by your HQ organisation. Multiple selection allowed',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'fullname',
                  placeholder: 'Full Name',
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: false,
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'shortnames',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'Product Short Name',
            fieldDescription:
              'Enter or select any product name(s) referenced in the ' + "'" + 'Product Short Name' + "'" + ' field of the last version of products owned by your HQ organisation. Multiple selection allowedd',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'shortname',
                  placeholder: 'Short Name',
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: false,
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'genericnames',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'Product INN/Common Name',
            fieldDescription:
              'Enter or select any product name(s) referenced in the ' + "'" + 'Product INN/Common Name' + "'" + ' field of the last version of products owned by your HQ organisation. Multiple selection allowed',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'genericname',
                  placeholder: 'Generic Name',
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: false,
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'companynames',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'Product Company Name',
            fieldDescription:
              'Enter or select any MAH name(s) referenced in the ' + "'" + 'Product Company Name' + "'" + ' field of the last version of products owned by your HQ organisation. Multiple selection allowed',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'companyname',
                  placeholder: 'Company Name',
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: false,
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'evcodes',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'repeat',
          props: {
            hidden: true,
            label: 'EV Code',
            fieldDescription:
              'Enter or select ev code. Multiple selection allowed',
          },
          fieldArray: {
            fieldGroup: [
              {
                key: 'selected',
                type: 'bulkLookupSelectorType',
                props: {
                  lookupname: 'evcode',
                  placeholder: 'EV Code',
                  searchPlaceholder: 'Look for entities and press Enter',
                  valueProp: 'code',
                  labelProp: 'name',
                  size: 5,
                  multiple: true,
                  showCode: true,
                  allowCount: true
                },
              },
              {
                key: 'datasource',
              },
              {
                key: 'operator',
                defaultValue: 'cn',
              },
              {
                key: 'term',
              },
              {
                key: 'lastOrder',
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },
            ],
          },
        },
      ],
    },
    { type: 'template', template: '<hr />' },
    {
      key: 'previews',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'custom-formly-checkbox flex-shrink-0 mt-2',
          key: 'filterStatus',
          type: 'checkbox',
          defaultValue: false,
          props: {
            attributes: {
              title: 'Select / Deselect filter',
            },
          },
        },
        {
          key: 'hasSelectedData',
          defaultValue: false,
        },
        {
          className: 'flex-grow-1',
          key: 'filterData',
          type: 'bulkLookupListType',
          props: {
            label: 'Preview List',
            fieldDescription:
              'Displays the list (maximum 1000 ev codes) of products retrieved as per specified filters. Multiple selection allowed',
            functionname: 'previewfnc',
            valueProp: 'code',
            labelProp: 'name',
            hidden: true,
            size: 15,
            showCode: true,
            showSearchField: false,
          },
        },
        {
          key: 'datasource',
        },
        {
          key: 'operator',
          defaultValue: 'cn',
        },
        {
          key: 'term',
        },
        {
          key: 'lastOrder',
        },
      ],
    },
    { type: 'template', template: '<hr />' },
  ];

  /**
   * Executes the callback every time Formly emits a change for this field,
   * regardless of hidden/visible state. Also ensures execution happens after
   * the formControl is initialized.
   */
  private onFieldVisible(field: FormlyFieldConfig, callback: () => void) {
    const changes$ = field.options!.fieldChanges!;
    const destroy$ = changes$.pipe(
      filter((e) => e.type === 'destroy' && e.field === field),
    );

    changes$
      .pipe(
        // Trigger on ANY change that belongs to this field
        filter((e) => e.field === field),
        takeUntil(destroy$),
      )
      .subscribe(() => {
        // Wait a microtask to ensure the control exists
        Promise.resolve().then(() => {
          callback();
        });
      });
  }

  private getSiblingControl(field: FormlyFieldConfig, key: string) {
    const group = field.parent?.fieldGroup;
    if (!group) return null;

    const sibling = group.find((f) => f.key === key);
    return sibling?.formControl ?? null;
  }
  /**
   * Helper: Runs logic once the field becomes visible and then subscribes
   * to valueChanges of a sibling/parent form control. Automatically:
   * - emits initial value via startWith()
   * - applies distinctUntilChanged()
   * - cleans up subscription via takeUntil(destroy$)
   */
  private onVisibleValueChange(
    field: FormlyFieldConfig,
    controlName: string,
    callback: (value: any) => void,
  ) {
    this.onFieldVisible(field, () => {
      Promise.resolve().then(() => {
        const ctrl = this.getSiblingControl(field, controlName);
        if (!ctrl) {
          console.warn(`Sibling control "${controlName}" not found.`);
          return;
        }

        const changes$ = field.options!.fieldChanges!;
        const destroy$ = changes$.pipe(
          filter((e) => e.type === 'destroy' && e.field === field),
        );

        ctrl.valueChanges
          .pipe(
            startWith(ctrl.value),
            distinctUntilChanged(),
            takeUntil(destroy$),
          )
          .subscribe(callback);
      });
    });
  }
  // ---------------------------------------------------------------
  // FIELD DEFINITION STARTS HERE
  // ---------------------------------------------------------------

  checkedList(componentID: string, checked: boolean) {
    if (checked) {
      if (!this.injector.get(Art57Section).bulkupdate.actionsCheckedRepeater.includes(componentID)) {
        this.injector.get(Art57Section).bulkupdate.actionsCheckedRepeater.push(componentID);
      }
    } else {
      let checkedValues = this.injector.get(Art57Section).bulkupdate.actionsCheckedRepeater.filter(item => item !== componentID);
      this.injector.get(Art57Section).bulkupdate.actionsCheckedRepeater = checkedValues;
    }

  }

  actions: FormlyFieldConfig[] = [
    {
      key: 'actionset',
      fieldGroupClassName: 'd-flex align-items-start',
      fieldGroup: [
        {
          className: 'flex-grow-1 repeater-action-scroll',
          key: 'action',
          type: 'repeatUpdate',
          fieldArray: {
            fieldGroup: [
              {
                key: 'action',
                type: 'bulkUpdateActionType',
              },
              {
                className: 'custom-formly-checkbox flex-shrink-0 mt-2',
                key: 'checked',
                type: 'checkbox',
                defaultValue: false,
                hideExpression: (model, formState, field) => {
                  const repeaterModel = field?.parent?.parent?.model;
                  return Array.isArray(repeaterModel) && repeaterModel.length === 1;
                },
                hooks: {
                  onInit: (field) => {
                    field.formControl!.valueChanges.subscribe((checked: boolean) => {
                      let componentID = field.parent?.model?.componentID;
                      this.checkedList(componentID, checked);
                    });
                  }
                }
              },
              {
                key: 'componentID',
                defaultValue: 'pending_init',
              },

              {
                fieldGroupClassName: 'row',
                fieldGroup: [
                  // -----------------------------------------------------
                  // LEFT COLUMN → CONDITIONS SECTION
                  // -----------------------------------------------------
                  {
                    className: 'col-repeater-bulkupdate repeater-filter-scroll',
                    key: 'condition',
                    type: 'bulkUpdateFilterRepeaterType',
                    defaultValue: [],
                    fieldArray: {
                      fieldGroupClassName: 'row g-2', // Bootstrap row + gap
                      fieldGroup: [
                        // -----------------------------------------------------
                        // (1) FIRST SELECT → parentpath
                        // -----------------------------------------------------
                        {
                          className: 'col-12',
                          key: 'parentpath',
                          type: 'inpselType',
                          props: {
                            placeholder: PLACEHOLDER[placeholderEnum.section],
                            required: true,
                            options:
                              this.injector.get(Art57Section).bulkupdate
                                .actionCache.actionOption,
                            valueProp: 'code',
                            labelProp: 'name',
                            componentType: 'select',
                          },
                          hooks: {
                            onInit: (field) => {
                              field.props!.options =
                                this.injector.get(
                                  Art57Section,
                                ).bulkupdate.actionCache.actionOption;
                            },
                          },
                        },

                        // -----------------------------------------------------
                        // (2) SECOND SELECT → xpath
                        // -----------------------------------------------------
                        {
                          className: 'col-12 col-md-6',
                          key: 'xpath',
                          type: 'inpselType',
                          props: {
                            placeholder: PLACEHOLDER[placeholderEnum.element],
                            required: true,
                            options: [],
                            valueProp: 'code',
                            labelProp: 'name',
                            componentType: 'select',
                          },
                          hooks: {
                            onInit: (field) => {
                              let oldValue: any = undefined;
                              // Subscribe when field becomes visible, then listen to parentpath changes
                              this.onVisibleValueChange(
                                field,
                                'parentpath',
                                (value: string) => {
                                  if (value && oldValue == value) {
                                    return;
                                  }

                                  field.props!.options = value
                                    ? this.findTerminalChildrenByPath(
                                      value,
                                      'F',
                                    )
                                    : [];

                                  // Reset relativexpath when the path changes
                                  if (field.model?.relativexpath) {
                                    field.model.relativexpath = undefined;
                                  }

                                  if (
                                    value != undefined &&
                                    oldValue != undefined
                                  ) {
                                    oldValue = value;
                                    field.formControl?.setValue(undefined);
                                  }
                                  oldValue = value;
                                },
                              );
                            },
                          },
                          expressions: {
                            hide: (field) => !field?.model?.parentpath,
                          },
                        },

                        // Pass-through field
                        { key: 'relativexpath' },

                        // -----------------------------------------------------
                        // (3) THIRD SELECT → match (predicate)
                        // -----------------------------------------------------
                        {
                          className: 'col-12 col-md-6',
                          key: 'match',
                          type: 'inpselType',
                          props: {
                            placeholder: PLACEHOLDER[placeholderEnum.predicate],
                            required: true,
                            options: [],
                            valueProp: 'code',
                            labelProp: 'name',
                            componentType: 'select',
                          },
                          hooks: {
                            onInit: (field) => {
                              let oldValue: any = undefined;
                              // React to xpath once field is visible
                              this.onVisibleValueChange(
                                field,
                                'xpath',
                                (value: string) => {
                                  if (value && oldValue == value) {
                                    return;
                                  }
                                  field.props!.options = value
                                    ? this.findFilterOperation(value)
                                    : [];

                                  // Compute a normalized relativexpath
                                  const prefix =
                                    'authorisedproducts/authorisedproduct/';
                                  field.model.relativexpath = value
                                    ? value.startsWith(prefix)
                                      ? value.substring(prefix.length)
                                      : value
                                    : undefined;
                                  if (
                                    value != undefined &&
                                    oldValue != undefined
                                  ) {
                                    oldValue = value;
                                    field.formControl?.setValue(undefined);
                                  }
                                  oldValue = value;
                                },
                              );
                            },
                          },
                          expressions: {
                            hide: (field) => !field?.model?.xpath,
                          },
                        },

                        // ------------------------------------------------------------
                        // VALUE SELECT BELOW (searchvalue)
                        // ------------------------------------------------------------
                        {
                          className: 'col-12',
                          key: 'searchvalue',
                          type: 'inpselType',
                          props: {
                            placeholder: PLACEHOLDER[placeholderEnum.chooseValue],
                            required: true,
                            options: [],
                            valueProp: 'code',
                            labelProp: 'name',
                            componentType: 'select',
                          },
                          hooks: {
                            onInit: (field) => {
                              let oldValue: any = undefined;
                              // React to xpath once field is visible
                              this.onVisibleValueChange(
                                field,
                                'match',
                                (value: string) => {
                                  if (value && oldValue == value) {
                                    return;
                                  }
                                  // If the predicate requires free text
                                  if (
                                    value &&
                                    field.model?.match &&
                                    ['CONTAINS', 'DOESNTCONTAIN'].includes(
                                      field.model.match,
                                    )
                                  ) {
                                    field.props!['componentType'] = 'input';
                                    field.props!.placeholder = PLACEHOLDER[placeholderEnum.enterValue];
                                  } else {
                                    field.props!['componentType'] = 'select';
                                    field.props!.placeholder = PLACEHOLDER[placeholderEnum.chooseValue];
                                    // Otherwise populate dropdown based on xpath
                                    field.props!.options = field.model.xpath
                                      ? this.getChooseableValues(
                                        field.model.xpath,
                                      )
                                      : [];
                                  }
                                  if (
                                    value != undefined &&
                                    oldValue != undefined
                                  ) {
                                    oldValue = value;
                                    field.formControl?.setValue(undefined);
                                  }
                                  oldValue = value;
                                },
                              );
                            },
                          },
                          expressions: {
                            hide: (field) =>
                              !field?.model?.match ||
                              ['ISMISSING', 'ISNOTMISSING'].includes(
                                field.model.match,
                              ),
                          },
                        },
                      ],
                    },
                  },

                  // -----------------------------------------------------
                  // MIDDLE VERTICAL DIVIDER
                  // -----------------------------------------------------
                  {
                    template: '<div class="vertical-divider"></div>',
                    className: 'col-auto px-0 d-flex vertical-divider',
                  },

                  // -----------------------------------------------------
                  // RIGHT COLUMN → OPERATIONS SECTION
                  // -----------------------------------------------------
                  {
                    className: 'col-repeater-bulkupdate repeater-change-scroll',
                    key: 'operation',
                    type: 'bulkUpdateChangeRepeaterType',
                    defaultValue: [],
                    fieldArray: {
                      fieldGroupClassName: 'row g-2', // Bootstrap row + gap
                      fieldGroup: [
                        // -----------------------------------------------------
                        // (1) FIRST SELECT → parentpath
                        // -----------------------------------------------------
                        {
                          className: 'col-12',
                          key: 'parentpath',
                          type: 'inpselType',
                          props: {
                            placeholder: PLACEHOLDER[placeholderEnum.section],
                            required: true,
                            options:
                              this.injector.get(Art57Section).bulkupdate
                                .actionCache.actionOption,
                            valueProp: 'code',
                            labelProp: 'name',
                            componentType: 'select',
                          },
                          hooks: {
                            onInit: (field) => {
                              field.props!.options =
                                this.injector.get(
                                  Art57Section,
                                ).bulkupdate.actionCache.actionOption;
                            },
                          },
                        },

                        // -----------------------------------------------------
                        // (2) GROUP WITH XPATH + TYPE
                        // -----------------------------------------------------
                        // -------------------------------------------------
                        // xpath
                        // -------------------------------------------------
                        {
                          className: 'col-12 col-md-6',
                          key: 'xpath',
                          type: 'inpselType',
                          props: {
                            placeholder: PLACEHOLDER[placeholderEnum.element],
                            required: true,
                            options: [],
                            valueProp: 'code',
                            labelProp: 'name',
                            componentType: 'select',
                          },
                          hooks: {
                            onInit: (field) => {
                              // Wait until visible, then subscribe to parentpath changes
                              let oldValue: any = undefined;
                              this.onVisibleValueChange(
                                field,
                                'parentpath',
                                (value: string) => {
                                  if (value && oldValue == value) {
                                    return;
                                  }
                                  field.props!.options = value
                                    ? this.findTerminalChildrenByPath(
                                      value,
                                      'C',
                                    )
                                    : [];

                                  // Clear relativexpath
                                  if (field.model?.relativexpath) {
                                    field.model.relativexpath = undefined;
                                  }
                                  if (
                                    value != undefined &&
                                    oldValue != undefined
                                  ) {
                                    oldValue = value;
                                    field.formControl?.setValue(undefined);
                                  }
                                  oldValue = value;
                                },
                              );
                            },
                          },
                          expressions: {
                            hide: (field) => !field?.model?.parentpath,
                          },
                        },

                        // bridge key
                        { key: 'relativexpath' },

                        // -------------------------------------------------
                        // (3) type (CHANGE, ASSIGN, REMOVE, etc.)
                        // -------------------------------------------------
                        {
                          className: 'col-12 col-md-6',
                          key: 'type',
                          type: 'inpselType',
                          props: {
                            placeholder: PLACEHOLDER[placeholderEnum.chooseAction],
                            required: true,
                            options: [],
                            valueProp: 'code',
                            labelProp: 'name',
                            componentType: 'select',
                          },
                          hooks: {
                            onInit: (field) => {
                              let oldValue: any = undefined;
                              // React to xpath value when visible
                              this.onVisibleValueChange(
                                field,
                                'xpath',
                                (value: string) => {
                                  if (value && oldValue == value) {
                                    return;
                                  }
                                  field.props!.options = value
                                    ? this.findChangeAction(value)
                                    : [];

                                  // Normalize relativexpath
                                  const prefix =
                                    'authorisedproducts/authorisedproduct/';
                                  field.model.relativexpath = value
                                    ? value.startsWith(prefix)
                                      ? value.substring(prefix.length)
                                      : value
                                    : undefined;
                                  if (
                                    value != undefined &&
                                    oldValue != undefined
                                  ) {
                                    oldValue = value;
                                    field.formControl?.setValue(undefined);
                                  }
                                  oldValue = value;
                                },
                              );
                            },
                          },
                          expressions: {
                            hide: (field) => !field?.model?.xpath,
                          },
                        },

                        // -------------------------------------------------
                        // (4) oldvalue
                        // -------------------------------------------------
                        {
                          className: 'col-12',
                          key: 'oldvalue',
                          type: 'inpselType',
                          props: {
                            label: 'From',
                            placeholder: PLACEHOLDER[placeholderEnum.chooseValue],
                            required: true,
                            options: [],
                            valueProp: 'code',
                            labelProp: 'name',
                            componentType: 'select',
                          },
                          hooks: {
                            onInit: (field) => {
                              let oldValue: any = undefined;
                              this.onVisibleValueChange(
                                field,
                                'type',
                                (value: string) => {
                                  if (value && oldValue == value) {
                                    return;
                                  }

                                  if (!['ADD', 'REMOVE'].includes(value)) {
                                    // Clear leaf flag
                                    if (field.model) delete field.model.leaf;
                                  }
                                  field.props!.placeholder = PLACEHOLDER[placeholderEnum.chooseValue];

                                  switch (value) {
                                    case 'CHANGE':

                                      field.props!.label = 'From';
                                      field.props!.required = true;

                                      field.props!.placeholder = PLACEHOLDER[placeholderEnum.chooseValue];
                                      field.props!['valueProp'] = 'code';
                                      field.props!['labelProp'] = 'name';
                                      field.props!.options = this.getChooseableValues(field.model.xpath);
                                      break;
                                    case 'REMOVE':
                                      const nodeInfo = this.getNodeInfo(field.model.xpath)
                                      const leafNodeInfo = this.getLeafNodeInfo(
                                        field.model.xpath,
                                      );
                                      // BUG-324337: Hiding the component not only clears the `placeholder` but also clears other properties like `oldValue`. Instead of hiding it, let's reassign `field.props`.
                                      field.props = {
                                        ...field.props,
                                        label: leafNodeInfo.label,
                                        required: true,
                                        valueProp: 'code',
                                        labelProp: 'name',
                                        placeholder: '',
                                      };
                                      
                                      field.props!.options = [];
                                      
                                      field.props!.label = leafNodeInfo.label;
                                      field.model['leaf'] = leafNodeInfo.leaf;
                                      field.props!.required = true;
                                      field.options?.detectChanges?.(field);
                                      setTimeout(() => {
                                        // Added the new placeholder
                                        field.props = {
                                          ...field.props,
                                          placeholder: PLACEHOLDER[placeholderEnum.chooseValue],
                                          // Refresh dropdown options
                                          options: [
                                            { code: 'any', name: '-- Any Value --' },
                                            ...this.getChooseableValues(field.model.xpath),
                                          ],
                                        };
                                        field.options?.detectChanges?.(field);
                                      });

                                      if (nodeInfo.componentType === 'datetime') {
                                        field.model['xpathDateFormat'] = nodeInfo.xpathDateFormat;
                                      }
                                      if (field.model && field.model.newvalue) delete field.model.newvalue;
                                      break;
                                  }

                                  if (
                                    value != undefined &&
                                    oldValue != undefined
                                  ) {
                                    oldValue = value;
                                    field.formControl?.setValue(undefined);
                                  }
                                  oldValue = value;
                                },
                              );
                            },
                          },
                          expressions: {
                            hide: (field) =>
                              !['CHANGE', 'REMOVE'].includes(
                                field?.model?.type,
                              ),
                          },
                        },

                        // -------------------------------------------------
                        // (5) dateformat (pass‑through, no UI)
                        // -------------------------------------------------
                        { key: 'dateformat' },

                        // -------------------------------------------------
                        // (6) date → feeds model.newvalue through expressionProperties
                        // -------------------------------------------------
                        {
                          key: 'date',
                          expressionProperties: {
                            // Keep original logic intact
                            'model.newvalue': (model) =>
                              model &&
                                model.newvalue === undefined &&
                                model.date &&
                                model.dateformat
                                ? DateUtil.importDate(
                                  model.date,
                                  model.dateformat,
                                )
                                : model === undefined ||
                                  model.newvalue === undefined
                                  ? undefined
                                  : model.newvalue,
                          },
                        },
                        {
                          key: 'leaf',
                        },
                        // -------------------------------------------------
                        // (7) newvalue (large switch on type)
                        // -------------------------------------------------
                        {
                          className: 'col-12',
                          key: 'newvalue',
                          type: 'inpselType',
                          props: {
                            label: 'Value',
                            required: true,
                            placeholder: PLACEHOLDER[placeholderEnum.enterValue],
                            componentType: 'input', // default
                          },

                          hooks: {
                            onInit: (field) => {
                              let oldValue: any = undefined;
                              this.onVisibleValueChange(
                                field,
                                'type',
                                (value: string) => {
                                  if (value && oldValue == value) {
                                    return;
                                  }

                                  if (!['ADD', 'REMOVE'].includes(value)) {
                                    // Clear leaf flag
                                    if (field.model) delete field.model.leaf;
                                  }

                                  let nodeInfo: any;
                                  field.props!['componentType'] = 'input';
                                  field.props!.placeholder = PLACEHOLDER[placeholderEnum.enterValue];
                                  switch (value) {
                                    // -------------------------------------------------
                                    // CHANGE
                                    // -------------------------------------------------
                                    case 'CHANGE':
                                      field.props!.label = 'To';
                                      field.props!.required = true;
                                      nodeInfo = this.getNodeInfo(
                                        field.model.xpath,
                                      );

                                      field.props!['componentType'] =
                                        nodeInfo.componentType;

                                      if (nodeInfo.componentType !== 'datetime') {
                                        delete field.model.xpathDateFormat;
                                        delete field.model.date;
                                        delete field.model.dateformat;
                                      }

                                      if (nodeInfo.componentType === 'select') {
                                        field.props!.placeholder = PLACEHOLDER[placeholderEnum.chooseValue];
                                        field.props!['valueProp'] = 'code';
                                        field.props!['labelProp'] = 'name';
                                        field.props!.options = this.injector
                                          .get(LookupGetService)
                                          .retrieveFromCache(
                                            nodeInfo.lookupName,
                                          );
                                      } else if (
                                        nodeInfo.componentType === 'datetime'
                                      ) {
                                        if (
                                          field.formControl &&
                                          !this.injector.get(Art57Section)
                                            .bulkupdate.isLoadingActionSet
                                        ) {
                                          setTimeout(() => {
                                            field?.props?.['clearDate']?.();
                                          });
                                        }
                                        field.props!.placeholder = PLACEHOLDER[placeholderEnum.enterValue];
                                        field.props!.required = true;
                                        field.props!['showFormat'] =
                                          nodeInfo.allowDateFormat;
                                        field.props!['keys'] = [
                                          'dateformat',
                                          'date'
                                        ];
                                        field.model['xpathDateFormat'] = nodeInfo.xpathDateFormat;
                                      }
                                      break;

                                    // -------------------------------------------------
                                    // ASSIGN
                                    // -------------------------------------------------
                                    case 'ASSIGN':
                                      nodeInfo = this.getNodeInfo(
                                        field.model.xpath,
                                      );
                                      field.props!['componentType'] =
                                        nodeInfo.componentType;

                                      if (nodeInfo.componentType !== 'datetime') {
                                        delete field.model.xpathDateFormat;
                                        delete field.model.date;
                                        delete field.model.dateformat;
                                      }


                                      field.props!.label = 'Value';

                                      if (nodeInfo.componentType === 'select') {
                                        field.props!.required = true;
                                        field.props!['valueProp'] = 'code';
                                        field.props!['labelProp'] = 'name';
                                        field.props!.placeholder = PLACEHOLDER[placeholderEnum.chooseValue];
                                        field.props!.options = this.injector
                                          .get(LookupGetService)
                                          .retrieveFromCache(
                                            nodeInfo.lookupName,
                                          );
                                      } else if (
                                        nodeInfo.componentType === 'datetime'
                                      ) {
                                        if (
                                          field.formControl &&
                                          !this.injector.get(Art57Section)
                                            .bulkupdate.isLoadingActionSet && !this.injector.get(Art57Section)
                                              .bulkupdate.actionSetLoaded
                                        ) {
                                          setTimeout(() => {
                                            field?.props?.['clearDate']?.();
                                          });
                                        }
                                        field.props!['placeholder'] = PLACEHOLDER[placeholderEnum.enterValue];
                                        field.props!.required = true;
                                        field.props!['showFormat'] =
                                          nodeInfo.allowDateFormat;
                                        field.props!['keys'] = [
                                          'dateformat',
                                          'date'
                                        ];
                                        field.model['xpathDateFormat'] = nodeInfo.xpathDateFormat;
                                      } else if (nodeInfo.componentType === 'input') {
                                        field.props!.placeholder = PLACEHOLDER[placeholderEnum.enterValue];
                                      }
                                      break;

                                    // -------------------------------------------------
                                    // PREPEND & APPEND
                                    // -------------------------------------------------
                                    case 'APPEND':
                                    case 'PREPEND':
                                      field.props!.required = true;
                                      field.props!.label = '';
                                      break;

                                    // -------------------------------------------------
                                    // ADD (leaf node logic)
                                    // -------------------------------------------------
                                    case 'ADD':
                                      const leafNodeInfo = this.getLeafNodeInfo(
                                        field.model.xpath,
                                      );
                                      field.props!.label = leafNodeInfo.label;
                                      field.props!.required = true;
                                      field.props!['componentType'] =
                                        leafNodeInfo.componentType;

                                      if (
                                        leafNodeInfo.componentType === 'select'
                                      ) {
                                        field.props!.options = this.injector
                                          .get(LookupGetService)
                                          .retrieveFromCache(
                                            leafNodeInfo.lookupName,
                                          );
                                        field.props!['valueProp'] = 'code';
                                        field.props!['labelProp'] = 'name';
                                        field.props!.placeholder = PLACEHOLDER[placeholderEnum.chooseValue];
                                      }

                                      field.model['leaf'] = leafNodeInfo.leaf;
                                      break;
                                  }

                                  if (
                                    value != undefined &&
                                    oldValue != undefined
                                  ) {
                                    oldValue = value;
                                    field.formControl?.setValue(undefined);
                                  }
                                  oldValue = value;
                                },
                              );
                            },
                          },

                          expressions: {
                            hide: (field) =>
                              ![
                                'ASSIGN',
                                'PREPEND',
                                'APPEND',
                                'CHANGE',
                                'ADD',
                              ].includes(field?.model?.type),
                          },
                        },
                      ],
                    },
                  },
                ],
              },
            ],
          },
        },
      ],
    },
  ];
}
